// Performance Optimization Service
// Implements caching, lazy loading, and performance monitoring

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface PerformanceMetrics {
  component: string;
  renderTime: number;
  timestamp: number;
  props?: any;
}

class PerformanceOptimizationService {
  private cache = new Map<string, CacheEntry<any>>();
  private performanceMetrics: PerformanceMetrics[] = [];
  private observers = new Map<string, PerformanceObserver>();
  
  // Cache Management
  public setCache<T>(key: string, data: T, ttl: number = 300000): void { // 5 minutes default TTL
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
    
    // Clean up expired entries periodically
    this.cleanupExpiredCache();
  }

  public getCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) return null;
    
    // Check if expired
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  public clearCache(pattern?: string): void {
    if (pattern) {
      // Clear cache entries matching pattern
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  private cleanupExpiredCache(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  // Image Optimization
  public optimizeImageUrl(url: string, width?: number, height?: number, quality: number = 80): string {
    if (!url) return url;
    
    // For demo purposes, we'll just add query parameters
    // In a real app, this would integrate with a CDN like Cloudinary or ImageKit
    const separator = url.includes('?') ? '&' : '?';
    let optimizedUrl = url;
    
    if (width) optimizedUrl += `${separator}w=${width}`;
    if (height) optimizedUrl += `&h=${height}`;
    optimizedUrl += `&q=${quality}&auto=format`;
    
    return optimizedUrl;
  }

  public preloadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = url;
    });
  }

  public preloadImages(urls: string[]): Promise<void[]> {
    return Promise.allSettled(urls.map(url => this.preloadImage(url)))
      .then(results => results.map(() => undefined));
  }

  // Component Performance Monitoring
  public measureComponentRender(componentName: string, renderFn: () => void, props?: any): void {
    const startTime = performance.now();
    
    renderFn();
    
    const endTime = performance.now();
    const renderTime = endTime - startTime;
    
    this.performanceMetrics.push({
      component: componentName,
      renderTime,
      timestamp: Date.now(),
      props
    });
    
    // Keep only recent metrics (last 1000 entries)
    if (this.performanceMetrics.length > 1000) {
      this.performanceMetrics = this.performanceMetrics.slice(-1000);
    }
    
    // Log slow renders in development
    if (process.env.NODE_ENV === 'development' && renderTime > 16) {
      console.warn(`Slow render detected: ${componentName} took ${renderTime.toFixed(2)}ms`);
    }
  }

  public getComponentMetrics(componentName?: string): PerformanceMetrics[] {
    if (componentName) {
      return this.performanceMetrics.filter(m => m.component === componentName);
    }
    return [...this.performanceMetrics];
  }

  public getAverageRenderTime(componentName: string): number {
    const metrics = this.getComponentMetrics(componentName);
    if (metrics.length === 0) return 0;
    
    const totalTime = metrics.reduce((sum, m) => sum + m.renderTime, 0);
    return totalTime / metrics.length;
  }

  // Debouncing and Throttling
  public debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  public throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let lastRan: number;
    
    return (...args: Parameters<T>) => {
      if (!lastRan) {
        func(...args);
        lastRan = Date.now();
      } else {
        clearTimeout(timeout);
        const timeout = setTimeout(() => {
          if (Date.now() - lastRan >= limit) {
            func(...args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  }

  // Virtual Scrolling Helper
  public calculateVisibleRange(
    containerHeight: number,
    itemHeight: number,
    scrollTop: number,
    overscan: number = 5
  ): { start: number; end: number } {
    const start = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const visibleCount = Math.ceil(containerHeight / itemHeight);
    const end = start + visibleCount + overscan * 2;
    
    return { start, end };
  }

  // Bundle Size Optimization
  public async loadComponent<T>(
    importFn: () => Promise<{ default: T }>
  ): Promise<T> {
    try {
      const module = await importFn();
      return module.default;
    } catch (error) {
      console.error('Failed to load component:', error);
      throw error;
    }
  }

  // Memory Management
  public measureMemoryUsage(): MemoryInfo | null {
    if ('memory' in performance) {
      return (performance as any).memory;
    }
    return null;
  }

  public cleanupUnusedResources(): void {
    // Clear expired cache
    this.cleanupExpiredCache();
    
    // Clear old performance metrics
    const oneHourAgo = Date.now() - 3600000;
    this.performanceMetrics = this.performanceMetrics.filter(
      m => m.timestamp > oneHourAgo
    );
    
    // Disconnect observers
    for (const observer of this.observers.values()) {
      observer.disconnect();
    }
    this.observers.clear();
  }

  // API Response Caching
  public async cachedFetch<T>(
    url: string,
    options?: RequestInit,
    ttl: number = 300000
  ): Promise<T> {
    const cacheKey = `fetch_${url}_${JSON.stringify(options)}`;
    
    // Check cache first
    const cached = this.getCache<T>(cacheKey);
    if (cached !== null) {
      return cached;
    }
    
    // Fetch and cache
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  }

  // Intersection Observer for Lazy Loading
  public createIntersectionObserver(
    callback: (entries: IntersectionObserverEntry[]) => void,
    options?: IntersectionObserverInit
  ): IntersectionObserver {
    const observer = new IntersectionObserver(callback, {
      threshold: 0.1,
      rootMargin: '50px',
      ...options
    });
    
    return observer;
  }

  // Resource Preloading
  public preloadResource(url: string, as: string): void {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = as;
    document.head.appendChild(link);
  }

  public prefetchResource(url: string): void {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  }

  // Performance Reporting
  public getPerformanceReport(): {
    cacheSize: number;
    cacheHitRate: number;
    averageRenderTime: number;
    slowComponents: string[];
    memoryUsage?: MemoryInfo;
  } {
    const cacheSize = this.cache.size;
    
    // Calculate cache hit rate (simplified)
    const recentMetrics = this.performanceMetrics.filter(
      m => Date.now() - m.timestamp < 3600000 // Last hour
    );
    
    const averageRenderTime = recentMetrics.length > 0
      ? recentMetrics.reduce((sum, m) => sum + m.renderTime, 0) / recentMetrics.length
      : 0;
    
    // Find slow components (render time > 16ms)
    const slowComponents = Array.from(new Set(
      recentMetrics
        .filter(m => m.renderTime > 16)
        .map(m => m.component)
    ));
    
    return {
      cacheSize,
      cacheHitRate: 0.85, // Mock value
      averageRenderTime,
      slowComponents,
      memoryUsage: this.measureMemoryUsage() || undefined
    };
  }
}

// Create singleton instance
export const performanceService = new PerformanceOptimizationService();

// React Hook for Performance Monitoring
export const usePerformanceMonitoring = (componentName: string) => {
  const measureRender = (renderFn: () => void, props?: any) => {
    performanceService.measureComponentRender(componentName, renderFn, props);
  };

  const getMetrics = () => {
    return performanceService.getComponentMetrics(componentName);
  };

  const getAverageRenderTime = () => {
    return performanceService.getAverageRenderTime(componentName);
  };

  return {
    measureRender,
    getMetrics,
    getAverageRenderTime
  };
};

// React Hook for Caching
export const useCache = <T>() => {
  const setCache = (key: string, data: T, ttl?: number) => {
    performanceService.setCache(key, data, ttl);
  };

  const getCache = (key: string): T | null => {
    return performanceService.getCache<T>(key);
  };

  const clearCache = (pattern?: string) => {
    performanceService.clearCache(pattern);
  };

  return {
    setCache,
    getCache,
    clearCache
  };
};

// React Hook for Debouncing
export const useDebounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
) => {
  return performanceService.debounce(func, delay);
};

// React Hook for Throttling
export const useThrottle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
) => {
  return performanceService.throttle(func, limit);
};

export default performanceService;